import useEntities from "entities/hooks/use-entities";
import { useMemo } from "react";
import { get, uniqBy } from 'lodash';
import useEntity from "entities/hooks/use-entity";

const getDuration = (clip = {}) => {
    const { 'trim-start': trimStartAt, 'trim-end': trimEndAt, 'image-duration': duration } = clip;

    if (duration) return duration;
    if (!trimEndAt) return 3.5;
    return trimEndAt - trimStartAt;
};

export const useVideoClipDuration = (id) => {
    const celebrationClip = useEntity({ type: 'watch-me-grow-video-clips', id });
    const video = useEntity({ type: 'videos', id: celebrationClip?.video?.id });

    if (!video) return 0;
    return video.duration;
};

export const useVideoClipUploadStatus = (id, isForImage = false) => {
    if (isForImage) {
        const celebrationClip = useEntity({ type: 'watch-me-grow-image-clips', id });
        const image = useEntity({ type: 'images', id: celebrationClip?.image?.id });
        if (!image) return false;
        return image.state;
    }

    const celebrationClip = useEntity({ type: 'watch-me-grow-video-clips', id });
    const video = useEntity({ type: 'videos', id: celebrationClip?.video?.id });
    // TODO: this returns either false or a string depending on if the clip is a video
    // could be cleaned up w/ consistent return type and/or extended to use for other clip types
    if (!video) return false;
    return video.state;
};

function convertObjectToArray(data) {
    return Object.values(data);
}

export const useCelebrationClips = (id) => {

    const allVideos = useEntities('videos')
    const allImages = useEntities('images')
    const filteredAllVideos = convertObjectToArray(allVideos)
    const filteredAllImages = convertObjectToArray(allImages)
    const imageClips = useEntities('watch-me-grow-image-clips');
    const videoClips = useEntities('watch-me-grow-video-clips');
    const filteredImageClips = convertObjectToArray(imageClips).filter(({ image }) => Object.keys(image) != 0)
    const filteredVideoClips = convertObjectToArray(videoClips).filter(({ video }) => Object.keys(video) != 0)

    const clips = { 'image-clips': filteredAllImages, 'video-clips': filteredAllVideos };
    let clipsWithStatus = [];
    if (id) {
        clipsWithStatus = [
            ...(filteredImageClips || []),
            ...(filteredVideoClips || []),
        ].map((el) => {
            const clip = { ...el };
            const filteredClip = clips[!!clip?.image ? 'image-clips' : 'video-clips'].find(({ id }) => id === clip?.[!!clip?.image ? 'image' : 'video']?.id)

            clip.duration = filteredClip?.duration / 1000;
            clip.uploadStatus = filteredClip?.state;
            return clip;
        });
    }

    return useMemo(() => {
        if (!id) return []

        return uniqBy(
            clipsWithStatus.filter(stat => stat.uploadStatus === 'finished').sort((a, b) => {
                return (a.position || 0) - (b.position || 0);
            }),
            'id'
        );
    }, [clips, id, clipsWithStatus]);
};

export const useClipEntities = () => {
    const videoClips = useEntities('watch-me-grow-video-clips');
    const imageClips = useEntities('watch-me-grow-image-clips');
    return useMemo(() => ({ ...videoClips, ...imageClips }), [videoClips, imageClips]);
};


export const useTimelineClips = (clips) => {
    const videoableClips = useClipEntities();
    return useMemo(() => {
        return uniqBy(
            clips.reduce((acc, el) => {
                const clip = videoableClips[el.id];
                if (acc.length === 0) {
                    const endAt = getDuration(clip);
                    return [{ startAt: 0, endAt, position: 0, ...clip }];
                }

                const [{ endAt: lastEndAt }] = acc.slice(-1);
                const endAt = lastEndAt + getDuration(clip);

                return [...acc, { startAt: lastEndAt, endAt, ...clip }];
            }, []),
            'id'
        );
    }, [clips, videoableClips]);
};